"use client";
import React, { useContext, useState, useEffect } from "react";
import styled from "styled-components";
import { device, useBreakpoints } from "../../lib/breakpoints";
import { useNavigate, useLocation, Link } from "react-router-dom";
import {
  MenuOutlined,
  CloseOutlined,
  CommentOutlined,
  IdcardOutlined,
  PoweroffOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Dropdown } from "antd";
import { useQueryClient } from "@tanstack/react-query";
import { trackEvent } from "../../helpers/mixpanel";
import { firebaseAuth } from "../../helpers/authentication";
import { userKeys } from "../../lib/queryKeys";
import {
  APP_NAME,
  BRAND_NAV_TAGLINE_BADGE,
  CLUMSE_OPEN_RETAIL_INTAKE_EVENT,
} from "../../helpers/constants";
import Logo from "../general/Logo";
import { ProfileDropdownMenuStyles } from "../general/ProfileDropdownMenuStyles";
import { AuthContext } from "../../context/AuthProvider";
import {
  DIALOG_NAME,
  useOpenParamDialog,
} from "../../helpers/useDialogSearchParam";

const IND_ORANGE = "#FF5722";
const SLATE_800 = "#1e293b";
const SLATE_900 = "#0f172a";
const SLATE_700 = "#334155";
const SLATE_300 = "#cbd5e1";
const WHITE = "#ffffff";
const BLACK = "#000000";

/** True on `/chat` and any homeowner Clumse chat thread (`/chat/:id`, `/chat/:id/:slug`, …). */
function isActiveClumseChatPath(pathname) {
  return pathname === "/chat" || pathname.startsWith("/chat/");
}

const MobileNavSheet = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  padding-top: var(--industrial-nav-stack-h);
  background: ${BLACK};
  border: none;
  box-shadow: none;
`;

const MobileNavSheetScroll = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: stretch;
  gap: 0.25rem;
  padding: 1rem 1.25rem max(1.5rem, env(safe-area-inset-bottom, 0px));
`;

/**
 * Black industrial command row + mobile sheet - same chrome as the former home-only nav.
 * Mounted from {@link MainLayout} for all routes where {@link showHeaderUi} is true.
 */
export default function IndustrialSiteNav() {
  const { isMobile } = useBreakpoints();
  const { user, userInfo } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const clumseChatNavActive = isActiveClumseChatPath(location.pathname);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const openLogin = useOpenParamDialog(DIALOG_NAME.login);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!isMobile || !mobileNavOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.body.classList.add("home-industrial-mobile-nav-open");
    return () => {
      document.body.style.overflow = prev;
      document.body.classList.remove("home-industrial-mobile-nav-open");
    };
  }, [isMobile, mobileNavOpen]);

  useEffect(() => {
    if (!isMobile || !mobileNavOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") setMobileNavOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isMobile, mobileNavOpen]);

  useEffect(() => {
    if (!isMobile) setMobileNavOpen(false);
  }, [isMobile]);

  const handleNavLogin = () => {
    setMobileNavOpen(false);
    trackEvent("Nav Login Click", {
      section: "industrial_nav",
      device_type: isMobile ? "mobile" : "desktop",
    });
    if (user) {
      navigate("/chat");
      return;
    }
    if (isMobile) {
      navigate("/login");
      return;
    }
    openLogin();
  };

  const handleNavGetStarted = () => {
    setMobileNavOpen(false);
    if (user) {
      trackEvent("Nav Get Started Click", {
        section: "industrial_nav",
        device_type: isMobile ? "mobile" : "desktop",
        destination: "chat",
      });
      navigate("/chat");
      return;
    }
    trackEvent("Nav Get Started Click", {
      section: "industrial_nav",
      device_type: isMobile ? "mobile" : "desktop",
      plan: "retail",
      path: location.pathname,
    });
    if (location.pathname === "/") {
      window.dispatchEvent(new CustomEvent(CLUMSE_OPEN_RETAIL_INTAKE_EVENT));
      return;
    }
    navigate("/", { state: { openFreeIntake: true } });
  };

  const onLogout = async () => {
    setMobileNavOpen(false);
    try {
      await firebaseAuth.signOut();
      queryClient.removeQueries({ queryKey: userKeys.user() });
      trackEvent("User Logged Out", {
        from_page: location.pathname,
        device_type: isMobile ? "mobile" : "desktop",
      });
      navigate("/");
    } catch (e) {
      console.error(e);
    }
  };

  const profileDropdownItems = [
    {
      key: "profile",
      label: (
        <Link to="/profile" onClick={() => setMobileNavOpen(false)}>
          <IdcardOutlined style={{ marginRight: 8 }} />
          Profile
        </Link>
      ),
    },
    { type: "divider" },
    {
      key: "logout",
      label: (
        <span
          role="button"
          tabIndex={0}
          onClick={onLogout}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") onLogout();
          }}
        >
          <PoweroffOutlined style={{ marginRight: 8 }} />
          Log out
        </span>
      ),
    },
  ];

  return (
    <IndustrialNav aria-label="Site">
      <ProfileDropdownMenuStyles />
      <IndustrialNavInner>
        <IndustrialNavLogoSlot>
          {user ? (
            <>
              <LogoHomeLink to="/chat" onClick={() => setMobileNavOpen(false)}>
                <Logo color="white" size="medium" hideIconFrame fullColorMark />
              </LogoHomeLink>
              <IndustrialNavTagline aria-hidden="true">
                {BRAND_NAV_TAGLINE_BADGE}
              </IndustrialNavTagline>
            </>
          ) : (
            <>
              <Logo color="white" size="medium" hideIconFrame fullColorMark />
              <IndustrialNavTagline aria-hidden="true">
                {BRAND_NAV_TAGLINE_BADGE}
              </IndustrialNavTagline>
            </>
          )}
        </IndustrialNavLogoSlot>
        {isMobile ? (
          <MobileMenuToggle
            type="button"
            aria-expanded={mobileNavOpen}
            aria-controls="home-industrial-nav-menu"
            aria-label={mobileNavOpen ? "Close menu" : "Open menu"}
            onClick={() => setMobileNavOpen((o) => !o)}
          >
            {mobileNavOpen ? (
              <CloseOutlined style={{ fontSize: "1.25rem" }} />
            ) : (
              <MenuOutlined style={{ fontSize: "1.25rem" }} />
            )}
          </MobileMenuToggle>
        ) : user ? (
          <IndustrialNavRight>
            <IndustrialNavChatLink
              to="/chat"
              $active={clumseChatNavActive}
              onClick={() =>
                trackEvent("industrial_nav_chat_click", {
                  section: "industrial_nav",
                })
              }
            >
              <CommentOutlined style={{ marginRight: "0.45rem" }} />
              Chat with Clumse
            </IndustrialNavChatLink>
            <NavAuthCluster>
              <Dropdown
                menu={{ items: profileDropdownItems }}
                placement="bottomRight"
                trigger={["click"]}
                overlayClassName="clumse-profile-menu"
              >
                <UserAvatarButton type="button" aria-label="Account menu">
                  {userInfo?.profileImage ? (
                    <img
                      src={userInfo.profileImage}
                      alt=""
                      width={32}
                      height={32}
                    />
                  ) : (
                    <DefaultAccountGlyph aria-hidden>
                      <UserOutlined />
                    </DefaultAccountGlyph>
                  )}
                </UserAvatarButton>
              </Dropdown>
            </NavAuthCluster>
          </IndustrialNavRight>
        ) : (
          <IndustrialNavRight>
            <IndustrialNavLinks>
              <IndustrialNavLink href="/#the-protocol">
                How it works
              </IndustrialNavLink>
              <IndustrialNavLink href="/#pricing">Pricing</IndustrialNavLink>
              <IndustrialNavLink href="/vendors/apply">For vendors</IndustrialNavLink>
            </IndustrialNavLinks>
            <NavAuthCluster>
              <NavLoginTextButton
                type="button"
                onClick={handleNavLogin}
                aria-label="Log in to your account"
              >
                LOGIN
              </NavLoginTextButton>
              <GetStartedButton
                type="button"
                onClick={handleNavGetStarted}
                aria-label="Get started with Clumse"
              >
                GET STARTED
              </GetStartedButton>
            </NavAuthCluster>
          </IndustrialNavRight>
        )}
      </IndustrialNavInner>
      {isMobile && mobileNavOpen ? (
        <MobileNavSheet
          id="home-industrial-nav-menu"
          role="dialog"
          aria-modal="true"
          aria-label="Site menu"
        >
          <MobileNavSheetScroll>
            {user ? (
              <>
                <MobileSheetChatLink
                  to="/chat"
                  $active={clumseChatNavActive}
                  onClick={() => setMobileNavOpen(false)}
                >
                  <CommentOutlined style={{ marginRight: "0.5rem" }} />
                  Chat with Clumse
                </MobileSheetChatLink>
                <MobileNavDivider />
                <MobileNavRouterLink
                  to="/profile"
                  onClick={() => setMobileNavOpen(false)}
                >
                  Profile
                </MobileNavRouterLink>
                <MobileNavDivider />
                <MobileNavAuthStack>
                  <NavLoginTextButton type="button" onClick={onLogout}>
                    Log out
                  </NavLoginTextButton>
                </MobileNavAuthStack>
              </>
            ) : (
              <>
                <MobileNavLink
                  href="/#the-protocol"
                  onClick={() => setMobileNavOpen(false)}
                >
                  How it works
                </MobileNavLink>
                <MobileNavLink
                  href="/#pricing"
                  onClick={() => setMobileNavOpen(false)}
                >
                  Pricing
                </MobileNavLink>
                <MobileNavLink
                  href="/vendors/apply"
                  onClick={() => setMobileNavOpen(false)}
                >
                  For vendors
                </MobileNavLink>
                <MobileNavDivider />
                <MobileNavAuthStack>
                  <NavLoginTextButton
                    type="button"
                    onClick={handleNavLogin}
                    aria-label="Log in to your account"
                  >
                    Login
                  </NavLoginTextButton>
                  <GetStartedButton
                    type="button"
                    onClick={handleNavGetStarted}
                    aria-label="Get started with Clumse"
                  >
                    GET STARTED
                  </GetStartedButton>
                </MobileNavAuthStack>
              </>
            )}
          </MobileNavSheetScroll>
        </MobileNavSheet>
      ) : null}
    </IndustrialNav>
  );
}

const LogoHomeLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  min-width: 0;
  text-decoration: none;
  color: inherit;
`;

const IndustrialNavChatLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  font-family: "Inter", system-ui, sans-serif;
  font-size: 0.8125rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  text-decoration: none;
  color: ${(p) => (p.$active ? WHITE : SLATE_300)};
  padding: 0.45rem 0.35rem;
  border-bottom: 2px solid
    ${(p) => (p.$active ? IND_ORANGE : "transparent")};
  transition: color 120ms linear, border-color 120ms linear;

  &:hover {
    color: ${WHITE};
  }
`;

const UserAvatarButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.28);
  background: ${BLACK};
  cursor: pointer;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &:focus-visible {
    outline: 2px solid ${IND_ORANGE};
    outline-offset: 2px;
  }
`;

const DefaultAccountGlyph = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: ${WHITE};
  font-size: 1.1rem;
`;

const MobileSheetChatLink = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: "Inter", system-ui, sans-serif;
  font-size: 0.9375rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  text-decoration: none;
  color: ${(p) => (p.$active ? WHITE : SLATE_300)};
  padding: 0.95rem 0.75rem;
  border: 1px solid ${(p) => (p.$active ? IND_ORANGE : SLATE_800)};
  background: ${(p) => (p.$active ? SLATE_900 : "transparent")};
  transition: color 120ms linear, border-color 120ms linear,
    background 120ms linear;

  &:hover {
    color: ${WHITE};
    border-color: ${IND_ORANGE};
  }
`;

const IndustrialNav = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  width: 100%;
  flex-shrink: 0;
  z-index: 999;
  background: ${BLACK};
`;

const IndustrialNavInner = styled.div`
  max-width: 1536px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-start;
  gap: 1rem 1.5rem;
  padding: 2rem clamp(1rem, 4vw, 4rem);
  /* Match MainLayout/AppLayout padding-top token so the fixed bar fills the slot. */
  min-height: var(--industrial-nav-stack-h);

  @media ${device.mobile} {
    flex-direction: row;
    align-items: center;
    padding: 1.25rem 1.25rem;
    justify-content: space-between;
    flex-wrap: nowrap;
    gap: 0.75rem;
    position: relative;
    z-index: 20;
    min-height: auto;
  }
`;

const IndustrialNavLogoSlot = styled.div`
  margin-right: auto;
  display: flex;
  align-items: center;
  gap: 0.65rem;
  min-width: 0;

  @media ${device.mobile} {
    margin-right: 0;
    min-width: 0;
  }
`;

const IndustrialNavTagline = styled.span`
  flex-shrink: 1;
  min-width: 0;
  font-family: var(--font-stack-mono, ui-monospace, monospace);
  font-size: clamp(0.45rem, 1.1vw, 0.58rem);
  font-weight: 800;
  letter-spacing: 0.04em;
  line-height: 1.2;
  text-transform: none;
  color: #ffffff;
  background: ${IND_ORANGE};
  border: 1px solid #000000;
  padding: 0.28rem 0.45rem;
  transform: rotate(-3deg);
  box-shadow: 2px 2px 0 rgba(0, 0, 0, 0.45);
  max-width: 11rem;
  text-align: center;

  @media (max-width: 380px) {
    display: none;
  }
`;

const MobileMenuToggle = styled.button`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 2.75rem;
  min-height: 2.75rem;
  margin: 0;
  padding: 0;
  border: 1px solid ${SLATE_800};
  border-radius: 0;
  background: transparent;
  color: ${WHITE};
  cursor: pointer;
  transition: border-color 120ms linear, color 120ms linear,
    background 120ms linear;

  &:hover {
    border-color: ${SLATE_700};
    color: ${WHITE};
    background: ${SLATE_900};
  }
`;

const mobileNavLinkStyles = `
  font-family: "Inter", system-ui, sans-serif;
  font-size: 0.9375rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  text-decoration: none;
  color: ${SLATE_300};
  padding: 0.85rem 0.25rem;
  border-radius: 0;
  text-align: center;
  transition: color 120ms linear;

  &:hover {
    color: ${WHITE};
  }
`;

const MobileNavLink = styled.a`
  ${mobileNavLinkStyles}
`;

const MobileNavRouterLink = styled(Link)`
  ${mobileNavLinkStyles}
  display: block;
`;

const MobileNavDivider = styled.div`
  height: 1px;
  margin: 0.65rem 0 0.5rem;
  background: ${SLATE_800};
`;

const IndustrialNavRight = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 1rem 2.25rem;
  flex-wrap: wrap;

  @media ${device.mobile} {
    width: 100%;
    margin-top: 0.75rem;
    gap: 0.75rem 1.25rem;
  }
`;

const IndustrialNavLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 1rem 1.75rem;
  min-width: 0;
`;

const IndustrialNavLink = styled.a`
  font-family: "Inter", system-ui, sans-serif;
  font-size: 0.8125rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  text-decoration: none;
  color: ${SLATE_300};
  border-radius: 0;
  transition: color 120ms linear, opacity 120ms linear;

  &:hover {
    color: ${WHITE};
  }
`;

const NavAuthCluster = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 0.65rem 1.1rem;
  flex-shrink: 0;

  @media (min-width: 1024px) {
    margin-left: 0.25rem;
    padding-left: 1.5rem;
    border-left: 1px solid rgba(148, 163, 184, 0.25);
  }
`;

const NavLoginTextButton = styled.button`
  margin: 0;
  padding: 0.35rem 0.15rem;
  border: none;
  background: transparent;
  cursor: pointer;
  font-family: "Inter", system-ui, sans-serif;
  font-size: 0.8125rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${SLATE_300};
  text-decoration: none;
  transition: color 120ms linear;

  &:hover {
    color: ${WHITE};
  }

  &:focus-visible {
    outline: 2px solid ${IND_ORANGE};
    outline-offset: 2px;
  }
`;

const GetStartedButton = styled.button`
  flex-shrink: 0;
  font-family: "Inter", system-ui, sans-serif;
  font-size: 0.75rem;
  font-weight: 900;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${WHITE};
  background: ${IND_ORANGE};
  border: 1px solid ${SLATE_800};
  border-radius: 0;
  padding: 0.65rem 1rem;
  cursor: pointer;
  box-shadow: 0 0 18px rgba(255, 87, 34, 0.35);

  &:hover {
    background: #f44e1b;
  }

  &:focus-visible {
    outline: 2px solid ${WHITE};
    outline-offset: 2px;
  }
`;

const MobileNavAuthStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.85rem;
  margin-top: 0.25rem;

  ${NavLoginTextButton} {
    display: block;
    width: 100%;
    text-align: center;
    padding: 0.65rem 0.25rem;
    font-size: 0.875rem;
    letter-spacing: 0.12em;
    text-transform: uppercase;
  }

  ${GetStartedButton} {
    display: inline-flex;
    width: 100%;
    justify-content: center;
  }
`;
